// Swimlane layout: steps in columns by how far along the flow they are, rows by lane; handoffs routed in the gutters.
// Nobody drags boxes around to keep a map tidy: change the flow or a step's lane and the map lays itself out again.
import { flowOrder, type MapBody, type Snapshot, type Step } from "@couragemfg/api/processes-model";

export const COLW = 184, LANEH = 112, PADX = 28;
export const SIZE: Record<Step["type"], { w: number; h: number }> = { task: { w: 148, h: 62 }, decision: { w: 140, h: 76 }, start: { w: 124, h: 42 }, end: { w: 124, h: 42 } };
export const sz = (s: Pick<Step, "type">) => SIZE[s.type] ?? SIZE.task;

export type Pos = { x: number; y: number; r: number; li: number };
export type Layout = { pos: Record<string, Pos>; rank: Record<string, number>; cols: number; back: Set<string>; ordered: string[]; W: number; H: number };

/** Positions from the reading order (flowOrder in the shared model): columns by how far along the flow, rows by lane. */
export function layout(d: Snapshot | MapBody): Layout {
  const { rank, back, ordered } = flowOrder(d);
  const li: Record<string, number> = {};
  d.lanes.forEach((l, i) => (li[l.id] = i));
  let cols = 1;
  const pos: Record<string, Pos> = {};
  for (const s of d.steps) {
    const r = rank[s.id], l = li[s.lane] ?? 0;
    cols = Math.max(cols, r + 1);
    pos[s.id] = { x: PADX + r * COLW + COLW / 2, y: l * LANEH + LANEH / 2, r, li: l };
  }
  return { pos, rank, cols, back, ordered, W: PADX * 2 + cols * COLW, H: Math.max(1, d.lanes.length) * LANEH };
}

function laneClear(L: Layout, l: number, r1: number, r2: number) {
  for (const id in L.pos) { const p = L.pos[id]; if (p.li === l && p.r > r1 && p.r < r2) return false; }
  return true;
}

/** An orthogonal path for a handoff, and where its label goes. */
export function route(L: Layout, from: Step, to: Step) {
  const a = L.pos[from.id], b = L.pos[to.id];
  const ax = a.x + sz(from).w / 2, ay = a.y, bx = b.x - sz(to).w / 2, by = b.y;
  if (L.back.has(`${from.id}>${to.id}`) || b.r <= a.r) {
    const yb = Math.max(a.li, b.li) * LANEH + LANEH - 9, ya = a.y + sz(from).h / 2, yt = b.y + sz(to).h / 2;
    return { d: `M${a.x} ${ya}V${yb}H${b.x}V${yt}`, lx: (a.x + b.x) / 2, ly: yb - 6, anchor: "middle" as const };
  }
  if (a.li === b.li) {
    if (laneClear(L, a.li, a.r, b.r)) return { d: `M${ax} ${ay}H${bx}`, lx: bx - 8, ly: by - 7, anchor: "end" as const };
    const yt = a.li * LANEH + 16; // low enough that a label above the line clears the lane's top edge
    return { d: `M${ax} ${ay}H${ax + 12}V${yt}H${bx - 12}V${by}H${bx}`, lx: ax + 16, ly: yt - 4, anchor: "start" as const };
  }
  const x1 = PADX + (a.r + 1) * COLW, x2 = PADX + b.r * COLW;
  const xv = laneClear(L, b.li, a.r, b.r) ? x1 : laneClear(L, a.li, a.r, b.r) ? x2 : x1;
  // The label goes where the handoff turns into its step's lane, so a decision's branches to different lanes each get
  // their own spot. When a step sits right there, it goes back beside the decision.
  const col = Math.round((xv - PADX) / COLW) - 1;
  const free = !Object.values(L.pos).some((p) => p.li === b.li && p.r === col);
  return { d: `M${ax} ${ay}H${xv}V${by}H${bx}`, lx: xv - 6, ly: free ? by - 7 : by > ay ? ay + 18 : ay - 9, anchor: "end" as const };
}

/** Up to `lines` lines of at most `max` characters, the last one ending in … when the text runs over. */
export function wrap(text: string, max: number, lines: number): string[] {
  const words = String(text || "").split(/\s+/).filter(Boolean), out: string[] = [];
  let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= max) cur += " " + w; else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  if (!out.length) out.push("Untitled");
  if (out.length > lines) {
    const keep = out.slice(0, lines);
    keep[lines - 1] = (keep[lines - 1] + " " + out.slice(lines).join(" ")).slice(0, max - 1).trimEnd() + "…";
    return keep;
  }
  return out.map((l) => (l.length > max ? l.slice(0, max - 1) + "…" : l));
}
